import RotatingSquare from '@src/svgs/RotatingSquare';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { ReelChatMessage } from './types';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type Props = {
  messages: ReelChatMessage[];
  onSendMessage: (text: string) => void;
  isLoading: boolean;
};

const ReelOutlineChatInterface: React.FC<Props> = ({
  messages,
  onSendMessage,
  isLoading,
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed || isLoading) return;
      onSendMessage(trimmed);
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
      }
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [inputValue, isLoading, onSendMessage],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleSuggestionClick = (text: string) => {
    if (isLoading) return;
    onSendMessage(text);
  };

  return (
    <Container data-testid="reel-chat-container">
      <Header>
        <HeaderTitle>Edit reel outline through chat</HeaderTitle>
      </Header>

      <MessagesContainer>
        <SuggestionsHeader>
          <SuggestionsText>
            Refine your reel outline. For example:
          </SuggestionsText>
          <SuggestionList>
            <Suggestion onClick={() => handleSuggestionClick('Try a different narrative structure')}>
              Try a different narrative structure
            </Suggestion>
            <Suggestion onClick={() => handleSuggestionClick('Make the beats shorter')}>
              Make the beats shorter
            </Suggestion>
            <Suggestion onClick={() => handleSuggestionClick('Focus more on...')}>
              Focus more on...
            </Suggestion>
            <Suggestion onClick={() => handleSuggestionClick('Add a chapter about...')}>
              Add a chapter about...
            </Suggestion>
          </SuggestionList>
        </SuggestionsHeader>

        {messages.map(message =>
          message.role === 'user' ? (
            <UserMessageContainer key={message.id}>
              <UserBubble>{message.content}</UserBubble>
            </UserMessageContainer>
          ) : (
            <AssistantMessage key={message.id}>
              {message.generationDetails ? (
                <GenerationSummary>
                  <AssistantText>{message.content}</AssistantText>
                  <GenerationOptions>
                    <OptionItem>
                      <OptionLabel>Description:</OptionLabel>
                      <OptionValue>{message.generationDetails.description}</OptionValue>
                    </OptionItem>
                    <OptionItem>
                      <OptionLabel>Duration:</OptionLabel>
                      <OptionValue>{formatDuration(message.generationDetails.targetDuration)}</OptionValue>
                    </OptionItem>
                    {message.generationDetails.speakers && message.generationDetails.speakers.length > 0 && (
                      <OptionItem>
                        <OptionLabel>Speakers:</OptionLabel>
                        <OptionValue>{message.generationDetails.speakers.join(', ')}</OptionValue>
                      </OptionItem>
                    )}
                    {message.generationDetails.goal && (
                      <OptionItem>
                        <OptionLabel>Goal:</OptionLabel>
                        <OptionValue>{message.generationDetails.goal}</OptionValue>
                      </OptionItem>
                    )}
                    {message.generationDetails.audiences && message.generationDetails.audiences.length > 0 && (
                      <OptionItem>
                        <OptionLabel>Audiences:</OptionLabel>
                        <OptionValue>{message.generationDetails.audiences.join(', ')}</OptionValue>
                      </OptionItem>
                    )}
                  </GenerationOptions>
                </GenerationSummary>
              ) : (
                message.content
              )}
            </AssistantMessage>
          ),
        )}

        {isLoading && (
          <LoadingIndicator>
            <RotatingSquare strokeColor="#a0a0a0" />
            <span>Arbor is generating the outline<span className="loading-dots" /></span>
          </LoadingIndicator>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer onSubmit={handleSubmit}>
        <TextArea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your revision request..."
          disabled={isLoading}
          data-testid="reel-chat-input"
        />
        <SendButton
          type="submit"
          disabled={!inputValue.trim() || isLoading}
          data-testid="reel-chat-send-button"
        >
          Send
        </SendButton>
      </InputContainer>
    </Container>
  );
};

export default ReelOutlineChatInterface;

// --- Styled Components (matching BlogView/ChatInterface.tsx patterns) ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--surface);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
`;

const HeaderTitle = styled.h3`
  color: var(--accent);
  font-size: 12px;
  letter-spacing: 0.5px;
  font-weight: 500;
  margin: 0 0 4px 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SuggestionsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
`;

const SuggestionsText = styled.p`
  color: var(--text-secondary);
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
`;

const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Suggestion = styled.div`
  color: #f2f3f5;
  font-size: 12px;
  padding-left: 8px;
  border-left: 2px solid var(--border);
  cursor: pointer;
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.7;
  }
`;

const UserMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const UserBubble = styled.div`
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 90%;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  background-color: var(--popover);
  color: var(--text-secondary);
  align-self: flex-end;
  border-bottom-right-radius: 4px;
`;

const AssistantMessage = styled.div`
  padding: 8px 0;
  max-width: 100%;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  align-self: flex-start;
`;

const AssistantText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const GenerationSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GenerationOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--sidebar-hover, rgba(255, 255, 255, 0.04));
  border-radius: 8px;
  border-left: 3px solid var(--primary);
`;

const OptionItem = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
`;

const OptionLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
  min-width: 80px;
`;

const OptionValue = styled.span`
  color: var(--text-secondary);
  font-weight: 400;
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: var(--text-secondary);
  font-size: 13px;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  svg {
    width: 16px;
    height: 16px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes loading-dots {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
  }

  .loading-dots::after {
    content: '';
    animation: loading-dots 1.5s steps(1) infinite;
  }
`;

const InputContainer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
  background-color: var(--surface);
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  background-color: var(--background);
  color: var(--foreground);
  outline: none;
  min-height: 40px;
  max-height: 120px;

  &:focus {
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SendButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: var(--primary);
  color: var(--primary-foreground, #fff);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
